import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { AlertCircle, Loader2, Plus } from 'lucide-react';
import { PageContainer } from '@/components/layout/PageContainer';
import { AppBreadcrumbs } from '@/components/layout/AppBreadcrumbs';
import { serviceSuggestionApiService } from '@/lib/api/serviceSuggestion.service';
import { serviceRequestApiService } from '@/lib/api/serviceRequest.service';
import type { ServiceDefinition } from '@om/shared-types';
import type { ServiceRequest } from '@om/shared-types';

const PRICE_RANGE_LABELS: Record<string, string> = {
  low: 'Lower Cost',
  mid: 'Mid Range',
  high: 'Premium',
};

const PRICE_RANGE_COLORS: Record<string, string> = {
  low: 'bg-green-100 text-green-700',
  mid: 'bg-amber-100 text-amber-700',
  high: 'bg-purple-100 text-purple-700',
};

const SR_STATUS_LABEL: Record<string, string> = {
  draft: 'Draft',
  open: 'Posted',
  quoted: 'Quoted',
  under_review: 'Under Review',
  awarded: 'Awarded',
  signed: 'Signed',
  closed: 'Closed',
  reopened: 'Reopened',
  cancelled: 'Cancelled',
};

const SR_STATUS_COLOR: Record<string, string> = {
  draft: 'text-gray-500',
  open: 'text-blue-700',
  quoted: 'text-purple-700',
  under_review: 'text-amber-700',
  awarded: 'text-green-700',
  signed: 'text-emerald-700 font-medium',
  closed: 'text-gray-500',
  reopened: 'text-blue-700',
  cancelled: 'text-red-600',
};

const SR_STATUS_PRIORITY: Record<string, number> = {
  draft: 1, open: 2, quoted: 3, under_review: 4,
  awarded: 5, signed: 6, closed: 0, reopened: 2, cancelled: 0,
};

function getHighestStatus(requests: ServiceRequest[]): string | null {
  if (requests.length === 0) return null;
  return requests.reduce((best, r) =>
    (SR_STATUS_PRIORITY[r.status] ?? 0) > (SR_STATUS_PRIORITY[best] ?? 0) ? r.status : best,
    requests[0].status
  );
}

function ServiceCard({ def, requests, onView }: {
  def: ServiceDefinition;
  requests: ServiceRequest[];
  onView: () => void;
}) {
  const { t } = useTranslation(['services', 'common']);
  const highestStatus = getHighestStatus(requests);

  return (
    <div className="flex flex-col rounded-xl border border-gray-200 bg-white p-4 shadow-sm">
      <div className="mb-2 flex flex-wrap gap-1.5">
        {def.typical_price_range && (
          <span className={`inline-flex items-center rounded-full px-2 py-0.5 text-[11px] font-medium ${PRICE_RANGE_COLORS[def.typical_price_range]}`}>
            {PRICE_RANGE_LABELS[def.typical_price_range]}
          </span>
        )}
      </div>

      <p className="text-sm font-semibold text-gray-900 leading-snug">{def.name}</p>

      {def.description && (
        <p className="mt-1 text-xs text-gray-500 line-clamp-2">{def.description}</p>
      )}

      <div className="mt-2 text-xs text-gray-500">
        {requests.length === 0 ? (
          t('services:myServicesPage.noRequestsYet')
        ) : (
          <>
            {t('services:myServicesPage.requestCount', { count: requests.length })}
            {highestStatus && (
              <span className={`ml-1.5 ${SR_STATUS_COLOR[highestStatus] ?? 'text-gray-600'}`}>
                · {SR_STATUS_LABEL[highestStatus] ?? highestStatus}
              </span>
            )}
          </>
        )}
      </div>

      <div className="mt-auto pt-3">
        <button
          type="button"
          onClick={onView}
          className="w-full rounded-md bg-gray-50 border border-gray-200 px-3 py-1.5 text-xs font-medium text-gray-700 hover:bg-gray-100 transition-colors"
        >
          {t('common:actions.view')} →
        </button>
      </div>
    </div>
  );
}

export function MyServicesPage() {
  const { celebrationId } = useParams<{ celebrationId: string }>();
  const navigate = useNavigate();
  const { t } = useTranslation(['services']);
  const cid = Number(celebrationId);

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [definitions, setDefinitions] = useState<ServiceDefinition[]>([]);
  const [allRequests, setAllRequests] = useState<ServiceRequest[]>([]);

  const loadData = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const [defs, requests] = await Promise.all([
        serviceSuggestionApiService.getCelebrationServiceDefinitions(cid),
        serviceRequestApiService.listRequests(cid).catch((): ServiceRequest[] => []),
      ]);
      setDefinitions(defs);
      setAllRequests(requests);
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : t('services:myServicesPage.loading'));
    } finally {
      setLoading(false);
    }
  }, [cid]);

  useEffect(() => { loadData(); }, [loadData]);

  const requestsByServiceId = useMemo(() => {
    const map = new Map<number, ServiceRequest[]>();
    for (const req of allRequests) {
      if (req.service_id == null) continue;
      if (!map.has(req.service_id)) map.set(req.service_id, []);
      map.get(req.service_id)!.push(req);
    }
    return map;
  }, [allRequests]);

  const byCategory = useMemo(() => {
    const map = new Map<string, ServiceDefinition[]>();
    for (const def of definitions) {
      if (!map.has(def.category)) map.set(def.category, []);
      map.get(def.category)!.push(def);
    }
    return map;
  }, [definitions]);

  if (loading) {
    return (
      <div className="flex h-64 items-center justify-center">
        <Loader2 className="h-5 w-5 animate-spin text-primary" />
        <span className="ml-2 text-sm text-gray-500">{t('services:myServicesPage.loading')}</span>
      </div>
    );
  }

  return (
    <PageContainer width="wide" className="space-y-6">
      <AppBreadcrumbs />

      {error && (
        <div className="flex items-center gap-2 rounded-lg border border-red-200 bg-red-50 px-4 py-2 text-sm text-red-700">
          <AlertCircle className="h-4 w-4 shrink-0" />
          {error}
        </div>
      )}

      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-xl font-semibold text-gray-900">{t('services:myServicesPage.title')}</h1>
          {definitions.length > 0 && (
            <p className="mt-0.5 text-sm text-gray-500">{t('services:myServicesPage.servicesSelected', { count: definitions.length })}</p>
          )}
        </div>
        <button
          type="button"
          onClick={() => navigate(`/celebrations/${cid}/services/edit`)}
          className="inline-flex items-center gap-1.5 rounded-md bg-primary px-3 py-2 text-sm font-medium text-white hover:bg-primary/90"
        >
          <Plus className="h-4 w-4" />
          {t('services:myServicesPage.addService')}
        </button>
      </div>

      {definitions.length === 0 ? (
        <div className="rounded-xl border border-dashed border-gray-300 bg-white p-10 text-center">
          <p className="text-sm text-gray-500">{t('services:myServicesPage.noServices')}</p>
          <button
            type="button"
            onClick={() => navigate(`/celebrations/${cid}/services/edit`)}
            className="mt-3 inline-flex items-center gap-1.5 rounded-md bg-primary px-4 py-2 text-sm font-medium text-white hover:bg-primary/90"
          >
            <Plus className="h-4 w-4" />
            {t('services:myServicesPage.addServices')}
          </button>
        </div>
      ) : (
        <div className="space-y-6">
          {Array.from(byCategory.entries()).map(([category, defs]) => (
            <section key={category}>
              <h2 className="mb-3 text-xs font-semibold uppercase tracking-widest text-gray-500">
                {category} ({defs.length})
              </h2>
              <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
                {defs.map(def => (
                  <ServiceCard
                    key={def.id}
                    def={def}
                    requests={requestsByServiceId.get(def.id) ?? []}
                    onView={() => navigate(`/celebrations/${cid}/services/${def.id}`)}
                  />
                ))}
              </div>
            </section>
          ))}
        </div>
      )}
    </PageContainer>
  );
}

